import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Text,
  useToast,
} from '@chakra-ui/react';
import AleaChevronRightIcon from '../../icons/AleaChevronRightIcon';
import AleaChevronDownIcon from '../../icons/AleaChevronDownIcon';
import AleaLabel from '../../components/AleaLabel/AleaLabel';
import { IGateway } from '../../interfaces/Gateway/IGateway';
import AleaDropdownAlpha from '../../components/AleaDropdownAlpha/AleaDropdownAlpha';
import AleaUnlinkIcon from '../../icons/AleaUnlinkIcon';
import GatewayService from '../../services/GatewayService';
import ClipService from '../../services/ClipService';
import ResidentService from '../../services/ResidentService';
import { IResidentListItem } from '../../interfaces/Resident/IResidentListItem';
import { useAuth } from '../../context/AuthContext';
import AleaResidentIcon from '../../icons/AleaResidentIcon';
import AleaDockingStationIcon from '../../icons/AleaDockingStationIcon';
import AleaClipOpenIcon from '../../icons/AleaClipOpenIcon';
import AleaPlusIcon from '../../icons/AleaPlusIcon';
import checkIcon from '/assets/images/icons/check.svg';
import { useTranslation } from 'react-i18next';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import { UserRole } from '../../constants/UserRoles.ts';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal';
import AleaProgressModal from '../../components/AleaProgressModal/AleaProgressModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface IClip {
  id: string;
  serial: string;
  ble_mac: string;
  model: string;
  batch: string;
  is_disposed: boolean;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  tenant_id: string | null;
  carehome_id: string;
  gateway_id: string;
  label: string;
}

interface HardwareTableProps {
  gateways: IGateway[];
  total: number;
  onRefresh: () => Promise<void>;
  selectedTag: string;
  isLoading?: boolean;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  onProgressModalChange?: (isOpen: boolean) => void;
  onStatsRefresh?: () => Promise<void>;
}

export type HardwareTableHandles = {
  hasPendingChanges: () => boolean;
  savePendingChanges: () => Promise<void>;
  discardPendingChanges: () => void;
  resetExpandedRow: () => void;
};

interface ExtendedGateway extends Omit<IGateway, 'clips'> {
  expandIcon?: string;
  livingArea?: string;
  clips?: IClip[];

  [key: string]: unknown;
}

const HardwareTable = forwardRef<HardwareTableHandles, HardwareTableProps>(({
  gateways,
  total,
  onRefresh,
  selectedTag,
  isLoading = false,
  onSort,
  orderBy,
  sortOrder,
  onProgressModalChange,
  onStatsRefresh,
}, ref) => {
  const { t } = useTranslation();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [showNewClip, setShowNewClip] = useState<Record<string, boolean>>({});
  const [selectedResidents, setSelectedResidents] = useState<
    Record<string, string>
  >({});
  const [selectedClips, setSelectedClips] = useState<Record<string, string>>(
    {}
  );
  const toast = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pendingClips, setPendingClips] = useState<Record<string, string[]>>(
    {}
  );

  // Add pending unlink states
  const [pendingResidentUnlinks, setPendingResidentUnlinks] = useState<
    Record<string, boolean>
  >({});
  const [pendingClipUnlinks, setPendingClipUnlinks] = useState<
    Record<string, boolean>
  >({});
  
  // Add modal state for unsaved changes warning
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingRowAction, setPendingRowAction] = useState<{
    targetRowId: string;
    action: 'close' | 'switch';
  } | null>(null);
  
  // Add progress modal state
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  // Add confirmation modal state for deactivate/reactivate
  const [confirmAction, setConfirmAction] = useState<{
    type: 'deactivate' | 'reactivate';
    gateway: ExtendedGateway;
  } | null>(null);

  const residentsQuery = useQuery({
    queryKey: ['hardwareTableResidents', user?.carehome?.id] as const,
    enabled: selectedTag === 'free' && !!user?.carehome?.id,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () =>
      ResidentService.getResidentList({
        carehome_id: user!.carehome!.id,
        without_gateway: true,
      }),
  });

  const residents: IResidentListItem[] = residentsQuery.data?.items ?? [];

  const unassignedClipsQuery = useQuery({
    queryKey: ['hardwareTableUnassignedClips', user?.carehome?.id] as const,
    enabled: !!user?.carehome?.id,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: () =>
      ClipService.getClips({
        is_active: true,
        unassigned: true,
        carehome_id: user!.carehome!.id,
      }),
    select: (response) =>
      response.items.map((clip) => ({
        label: clip.serial,
        value: clip.id,
      })),
  });

  const unassignedClips = unassignedClipsQuery.data ?? [];
  const loadingClips = unassignedClipsQuery.isLoading || unassignedClipsQuery.isFetching;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    hasPendingChanges: () => {
      return Object.keys(pendingClips).length > 0 ||
             Object.keys(selectedResidents).length > 0 ||
             Object.keys(pendingResidentUnlinks).length > 0 ||
             Object.keys(pendingClipUnlinks).length > 0 ||
             Object.keys(selectedClips).length > 0;
    },
    savePendingChanges: async () => {
      if (expandedRowId) {
        const item = (gateways as ExtendedGateway[]).find(g => g.id === expandedRowId);
        if (item) {
          await handleSubmitClips(item, false);
        }
      }
    },
    discardPendingChanges: () => {
      if (expandedRowId) {
        const item = (gateways as ExtendedGateway[]).find(g => g.id === expandedRowId);
        if (item) {
          handleCancelChanges(item);
        }
      }
    },
    resetExpandedRow: () => {
      setExpandedRowId(null);
    }
  }));

  // Notify parent about progress modal state changes
  useEffect(() => {
    if (onProgressModalChange) {
      onProgressModalChange(isProgressModalOpen);
    }
  }, [isProgressModalOpen, onProgressModalChange]);

  const handleUnlinkResident = async (gatewayId: string) => {
    // Add to pending unlinks instead of immediate API call
    setPendingResidentUnlinks((prev) => ({
      ...prev,
      [gatewayId]: true,
    }));
  };

  const handleUnlinkClip = async (clipId: string) => {
    // Add to pending unlinks instead of immediate API call
    setPendingClipUnlinks((prev) => ({
      ...prev,
      [clipId]: true,
    }));
  };

  const handleClipSelect = (gatewayId: string, clipSerial: string) => {
    const clipId = unassignedClips.find(
      (clip) => clip.label === clipSerial
    )?.value;
    if (!clipId) return;

    // Remove from pending unlinks if it was marked for unlinking
    setPendingClipUnlinks((prev) => {
      const newState = { ...prev };
      delete newState[clipId];
      return newState;
    });

    setPendingClips((prev) => ({
      ...prev,
      [gatewayId]: [...(prev[gatewayId] || []), clipId],
    }));

    setSelectedClips((prev) => ({
      ...prev,
      [gatewayId]: '', // Reset dropdown value after selection
    }));

    setShowNewClip((prev) => ({
      ...prev,
      [gatewayId]: false, // Hide the dropdown after selection
    }));
  };


  const executeDeactivateGateway = async (gateway: ExtendedGateway) => {
    setIsProgressModalOpen(true);
    setProgressMessage(t('savingChanges'));

    try {
      await GatewayService.updateGateway(gateway.id, {
        carehome_id: gateway.carehome_id,
        resident_id: gateway.resident_id || null,
        ble_mac: gateway.ble_mac,
        lte_imei: gateway.lte_imei,
        lte_iccid: gateway.lte_iccid,
        lte_imsi: gateway.lte_imsi,
        wifi_mac: gateway.wifi_mac,
        serial: gateway.serial,
        model: gateway.model,
        batch: gateway.batch,
        label: gateway.label,
        type: gateway.type,
        password: gateway.password,
        is_active: false,
      });

      toast({
        title: t('success'),
        description: t('general.changesSaved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await onRefresh();
      if (onStatsRefresh) {
        await onStatsRefresh();
      }
      setExpandedRowId(null);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('general.changesSaveFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Error deactivating gateway:', error);
    } finally {
      setIsProgressModalOpen(false);
      setProgressMessage('');
    }
  };

  const executeReactivateGateway = async (gateway: ExtendedGateway) => {
    setIsProgressModalOpen(true);
    setProgressMessage(t('savingChanges'));

    try {
      await GatewayService.updateGateway(gateway.id, {
        carehome_id: gateway.carehome_id,
        resident_id: gateway.resident_id || null,
        ble_mac: gateway.ble_mac,
        lte_imei: gateway.lte_imei,
        lte_iccid: gateway.lte_iccid,
        lte_imsi: gateway.lte_imsi,
        wifi_mac: gateway.wifi_mac,
        serial: gateway.serial,
        model: gateway.model,
        batch: gateway.batch,
        label: gateway.label,
        type: gateway.type,
        password: gateway.password,
        is_active: true,
      });

      toast({
        title: t('success'),
        description: t('general.changesSaved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await onRefresh();
      if (onStatsRefresh) {
        await onStatsRefresh();
      }
      setExpandedRowId(null);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('general.changesSaveFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Error reactivating gateway:', error);
    } finally {
      setIsProgressModalOpen(false);
      setProgressMessage('');
    }
  };

  const handleDeactivateGateway = (gateway: ExtendedGateway) => {
    setConfirmAction({ type: 'deactivate', gateway });
  };

  const handleReactivateGateway = (gateway: ExtendedGateway) => {
    setConfirmAction({ type: 'reactivate', gateway });
  };

  const handleSubmitClips = async (item: ExtendedGateway, autoCloseRow: boolean = true) => {
    const clipIds = pendingClips[item.id];
    const selectedResidentId = selectedResidents[item.id];
    const hasResidentUnlink = pendingResidentUnlinks[item.id];
    const hasClipUnlinks = item.clips?.some(clip => pendingClipUnlinks[clip.id]);
    
    // Check for clip replacements (pending unlink + new selection)
    const clipReplacements = item.clips?.filter(clip => 
      pendingClipUnlinks[clip.id] && selectedClips[`${item.id}_${clip.id}`]
    ) || [];
    
    const hasChanges = clipIds?.length || selectedResidentId || hasResidentUnlink || hasClipUnlinks || clipReplacements.length;

    if (!hasChanges) return;

    setIsProgressModalOpen(true);
    setProgressMessage(t('savingChanges'));

    try {
      // Handle resident unlink first
      if (hasResidentUnlink) {
        await GatewayService.deAssignGatewayFromResident(item.id);
      }

      // Handle clip unlinks (including those that will be replaced)
      if (hasClipUnlinks && item.clips) {
        const clipsToUnlink = item.clips.filter(clip => pendingClipUnlinks[clip.id]);
        await Promise.all(
          clipsToUnlink.map((clip) =>
            ClipService.deAssignClipFromGateway(clip.id)
          )
        );
      }

      // Save resident if selected (new assignment)
      if (selectedResidentId) {
        await GatewayService.assignGatewayToResident(
          item.id,
          selectedResidentId
        );
      }

      // Save clips if any (new assignments)
      if (clipIds?.length) {
        await Promise.all(
          clipIds.map((clipId) =>
            ClipService.assignClipToGateway(clipId, item.id)
          )
        );
      }

      // Handle clip replacements (assign new clips for those that were unlinked)
      if (clipReplacements.length) {
        await Promise.all(
          clipReplacements.map((clip) => {
            const newClipId = selectedClips[`${item.id}_${clip.id}`];
            return ClipService.assignClipToGateway(newClipId, item.id);
          })
        );
      }

      toast({
        title: t('success'),
        description: t('general.changesSaved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear all selections and pending states
      setPendingClips((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setSelectedClips((prev) => {
        const newState = { ...prev };
        // Clear clip replacement selections
        if (item.clips) {
          item.clips.forEach(clip => {
            delete newState[`${item.id}_${clip.id}`];
          });
        }
        delete newState[item.id];
        return newState;
      });
      setSelectedResidents((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setPendingResidentUnlinks((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setPendingClipUnlinks((prev) => {
        const newState = { ...prev };
        if (item.clips) {
          item.clips.forEach(clip => {
            delete newState[clip.id];
          });
        }
        return newState;
      });
      // Close the dropdown after successful save
      setShowNewClip((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });

      onRefresh();
      await queryClient.invalidateQueries({
        queryKey: ['hardwareTableUnassignedClips', user?.carehome?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['hardwareTableResidents', user?.carehome?.id],
      });
      await queryClient.invalidateQueries({ queryKey: ['hardwareAvailableClips'] });
      // Refresh care home stats after successful submit
      if (onStatsRefresh) {
        await onStatsRefresh();
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('general.changesSaveFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Error saving changes:', error);
    } finally {
      setIsProgressModalOpen(false);
      setProgressMessage('');
      setPendingRowAction(null);
      // Close the expanded row after successful submit only if autoCloseRow is true
      if (autoCloseRow) {
        setExpandedRowId(null);
      }
    }
  };

  const handleCancelChanges = (item: ExtendedGateway) => {
    // Show progress modal for cancel operation
    setProgressMessage(t('changesAreBeingDiscarded'));
    setIsProgressModalOpen(true);

    // Close the expanded row immediately
    setExpandedRowId(null);

    setTimeout(() => {
      // Clear all pending changes for this item
      setPendingResidentUnlinks((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setSelectedResidents((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setPendingClips((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });
      setPendingClipUnlinks((prev) => {
        const newState = { ...prev };
        if (item.clips) {
          item.clips.forEach(clip => {
            delete newState[clip.id];
          });
        }
        return newState;
      });
      setSelectedClips((prev) => {
        const newState = { ...prev };
        // Clear clip replacement selections
        if (item.clips) {
          item.clips.forEach(clip => {
            delete newState[`${item.id}_${clip.id}`];
          });
        }
        delete newState[item.id];
        return newState;
      });
      setShowNewClip((prev) => {
        const newState = { ...prev };
        delete newState[item.id];
        return newState;
      });

      // Hide progress modal
      setIsProgressModalOpen(false);
      setProgressMessage('');
    }, 2000); // 2 seconds for better UX perception
  };

  // Helper function to check if there are any pending changes
  const hasPendingChanges = (gatewayId: string) => {
    const gateway = (gateways as ExtendedGateway[]).find(g => g.id === gatewayId);
    return !!(
      pendingClips[gatewayId]?.length ||
      selectedResidents[gatewayId] ||
      pendingResidentUnlinks[gatewayId] ||
      gateway?.clips?.some(clip => pendingClipUnlinks[clip.id]) ||
      gateway?.clips?.some(clip => pendingClipUnlinks[clip.id] && selectedClips[`${gatewayId}_${clip.id}`])
    );
  };

  // Handle modal actions for unsaved changes
  const handleSaveAndProceed = async () => {
    setIsUnsavedChangesModalOpen(false);
    setProgressMessage(t('savingChanges'));
    setIsProgressModalOpen(true);
    
    try {
      if (expandedRowId) {
        const item = (gateways as ExtendedGateway[]).find(g => g.id === expandedRowId);
        if (item) {
          await handleSubmitClips(item, false); // Don't auto close, modal system handles it
        }
      }
      
      // Handle row actions after save (handleSubmitClips doesn't auto-close anymore)
      if (pendingRowAction?.action === 'close') {
        setExpandedRowId(null);
      } else if (pendingRowAction?.action === 'switch') {
        setExpandedRowId(pendingRowAction.targetRowId);
      }
    } finally {
      // handleSubmitClips already handles progress modal and row closing
      // Just clean up pending action
      setPendingRowAction(null);
    }
  };

  const handleDiscardAndProceed = () => {
    setIsUnsavedChangesModalOpen(false);
    setProgressMessage(t('changesAreBeingDiscarded'));
    setIsProgressModalOpen(true);
    
    setTimeout(() => {
      if (expandedRowId) {
        const item = (gateways as ExtendedGateway[]).find(g => g.id === expandedRowId);
        if (item) {
          handleCancelChanges(item);
        }
      }
      
      // Proceed with pending action
      if (pendingRowAction) {
        if (pendingRowAction.action === 'close') {
          setExpandedRowId(null);
        } else if (pendingRowAction.action === 'switch') {
          setExpandedRowId(pendingRowAction.targetRowId);
        }
      }
      
      setIsProgressModalOpen(false);
      setPendingRowAction(null);
    }, 1000); // 1000ms for better UX perception
  };

  // Just close modal without any action
  const handleCloseModal = () => {
    setIsUnsavedChangesModalOpen(false);
  };

  const columns: ITableColumn<ExtendedGateway>[] = [
    ...(user?.role !== UserRole.ORGANISATION_MANAGER
      ? [
          {
            key: 'expandIcon',
            label: '',
            sortable: false,
            width: '50px',
            render: (data: ExtendedGateway) => (
              <Box
                cursor="pointer"
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                {expandedRowId === data.id ? (
                  <AleaChevronDownIcon />
                ) : (
                  <AleaChevronRightIcon />
                )}
              </Box>
            ),
          },
        ]
      : []),
    {
      key: 'resident__name',
      label: t('residents'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '22%' : '25%',
      render: (data) => (
        <Text>
          {data.resident
            ? `${data.resident.last_name}, ${data.resident.first_name}`
            : '-'}
        </Text>
      ),
      onSort: onSort ? () => onSort('resident__name', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'resident__name' ? sortOrder : undefined,
    },
    {
      key: 'resident__room',
      label: t('room'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '8%' : '12%',
      render: (data) => <Text>{data?.resident?.room}</Text>,
      onSort: onSort ? () => onSort('resident__room', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'resident__room' ? sortOrder : undefined,
    },
    {
      key: 'resident__station__name',
      label: t('livingArea'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '12%' : '15%',
      render: (data) => <Text>{data.resident?.station?.name || '-'}</Text>,
      onSort: onSort ? () => onSort('resident__station__name', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'resident__station__name' ? sortOrder : undefined,
    },
    ...(user?.role === UserRole.ORGANISATION_MANAGER
      ? [
          {
            key: 'carehome',
            label: t('careHome'),
            sortable: false,
            width: '18%',
            render: (data: ExtendedGateway) => (
              <Text>{data?.carehome?.name || '-'}</Text>
            ),
            onSort: onSort ? () => onSort('carehome', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
            sortOrder: orderBy === 'carehome' ? sortOrder : undefined,
          },
        ]
      : []),
    {
      key: 'serial',
      label: t('dockingStation'),
      sortable: true,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '18%' : '22%',
      render: (data) => <AleaLabel>{data.serial}</AleaLabel>,
      onSort: onSort ? () => onSort('serial', sortOrder === 'asc' ? 'desc' : 'asc') : undefined,
      sortOrder: orderBy === 'serial' ? sortOrder : undefined,
    },
    {
      key: 'clips',
      label: t('clips'),
      sortable: false,
      width: user?.role === UserRole.ORGANISATION_MANAGER ? '22%' : '26%',
      render: (data) => (
        <Flex gap="12px" flexWrap="wrap" py="4px">
          {data.clips?.map((clip) => (
            <AleaLabel key={clip.id}>{clip.serial}</AleaLabel>
          ))}
        </Flex>
      ),
    },
  ];

  const renderExpandedRow = (item: ExtendedGateway) => {
    const residentOptions = residents.map((resident) => ({
      label: `${resident.last_name}, ${resident.first_name} (${resident.room})`,
      value: resident.id,
    }));

    return (
      <Box p={4}>
        <Box borderRadius="12px" py={6} px={24} fontSize="18px">
          <Grid templateColumns="1.2fr 0.8fr 1.2fr" gap={6}>
            <GridItem>
              <Box width="100%">
                <Flex direction="column" alignItems="flex-start">
                  <Box mb="15px">
                    <AleaResidentIcon size="75" />
                  </Box>
                  <Text fontSize="sm" color="gray.500" mb="8px">
                    {t(getLanguageKeyByType('residents', user?.carehome?.type))}
                  </Text>
                </Flex>
                <Flex direction="column" gap={2} width="100%">
                  {item.resident && !pendingResidentUnlinks[item.id] ? (
                    <Flex alignItems="center" gap={2} height="50px">
                      <Text textAlign="left" width="100%">
                        {`${item.resident.last_name}, ${item.resident.first_name} (${item.resident.room})`}
                      </Text>
                      <Box
                        cursor="pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnlinkResident(item.id);
                        }}
                        position="relative"
                        // Visual feedback for pending unlink
                        opacity={pendingResidentUnlinks[item.id] ? 0.5 : 1}
                        bg={pendingResidentUnlinks[item.id] ? 'red.100' : 'transparent'}
                        borderRadius="4px"
                        p={1}
                      >
                        <AleaUnlinkIcon size="16" />
                      </Box>
                    </Flex>
                  ) : (selectedTag === 'free' || pendingResidentUnlinks[item.id]) ? (
                    <>
                      <Box width="100%">
                        <AleaDropdownAlpha
                          label=""
                          value={selectedResidents[item.id] || ''}
                          options={residentOptions}
                          onChange={(value) => {
                            setSelectedResidents((prev) => ({
                              ...prev,
                              [item.id]: value,
                            }));
                          }}
                          placeholder={t(
                            getLanguageKeyByType(
                              'hardware.selectAResident',
                              user?.carehome?.type
                            )
                          )}
                          searchPlaceholder={t('hardware.selectAResident')}
                        />
                      </Box>
                    </>
                  ) : (
                    <Text
                      textAlign="left"
                      width="100%"
                      height="50px"
                      display="flex"
                      alignItems="center"
                    >
                      -
                    </Text>
                  )}
                </Flex>
              </Box>
            </GridItem>
            <GridItem>
              <Box width="100%">
                <Flex direction="column" alignItems="flex-start">
                  <Box mb="15px">
                    <AleaDockingStationIcon size="75" />
                  </Box>
                  <Text fontSize="sm" color="gray.500" mb="8px">
                    {t('dockingStation')}
                  </Text>
                </Flex>
                <Text
                  textAlign="left"
                  width="100%"
                  height="50px"
                  display="flex"
                  alignItems="center"
                >
                  {item.serial}
                </Text>
                {selectedTag === 'free' &&
                  (!item.clips || item.clips.length === 0) && (
                    <Text
                      color="#EF5C36"
                      fontSize="15px"
                      fontStyle="normal"
                      fontWeight="300"
                      lineHeight="normal"
                      mt="5px"
                      cursor="pointer"
                      textAlign="left"
                      textDecoration="underline"
                      onClick={(e) => {
                      e.stopPropagation();
                      handleDeactivateGateway(item);
                    }}
                  >
                    {t('deactivate')}
                  </Text>
                )}
              {selectedTag === 'deactivated' && (
                <Text
                  color="alea.500"
                  fontSize="15px"
                  fontStyle="normal"
                  fontWeight="300"
                  lineHeight="normal"
                  mt="5px"
                  cursor="pointer"
                  textAlign="left"
                  textDecoration="underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReactivateGateway(item);
                  }}
                >
                  {t('reactivate')}
                </Text>
              )}
              </Box>
            </GridItem>
            <GridItem>
              <Box width="100%">
                <Flex direction="column" alignItems="flex-start" pl="41px">
                  <Box mb="15px">
                    <AleaClipOpenIcon size="75" />
                  </Box>
                </Flex>
                <Flex direction="column" gap={25} width="100%">
                  {item.clips?.map((clip, index) => (
                    <Flex key={clip.id} direction="column" gap={2} width="100%">
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        pl="41px"
                        textAlign="left"
                      >
                        {t('clip')} {index + 1}
                      </Text>
                      <Flex alignItems="center" gap={25} width="100%">
                        {!pendingClipUnlinks[clip.id] ? (
                          // Normal state: show unlink icon + disabled dropdown with current clip
                          <>
                            <Box
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnlinkClip(clip.id);
                              }}
                              position="relative"
                              borderRadius="4px"
                              p={1}
                            >
                              <AleaUnlinkIcon size="16" />
                            </Box>
                            <Box flex={1}>
                              <AleaDropdownAlpha
                                label=""
                                value={clip.serial}
                                options={[{ label: clip.serial, value: clip.serial }]}
                                onChange={() => {}}
                                placeholder={t('hardware.selectAClip')}
                                searchPlaceholder={t('hardware.selectAClip')}
                                isDisabled={true}
                              />
                            </Box>
                          </>
                        ) : (
                          // Pending unlink state: hide icon + show active dropdown for new clip selection
                          <>
                            <Box width="16px" /> {/* Spacer to maintain alignment */}
                            <Box flex={1}>
                              <AleaDropdownAlpha
                                label=""
                                value={selectedClips[`${item.id}_${clip.id}`] || ''}
                                options={unassignedClips
                                  .filter(
                                    (unassignedClip) =>
                                      !pendingClips[item.id]?.includes(unassignedClip.value)
                                  )}
                                onChange={(value) => {
                                  setSelectedClips((prev) => ({
                                    ...prev,
                                    [`${item.id}_${clip.id}`]: value,
                                  }));
                                }}
                                placeholder={t('hardware.selectAClip')}
                                searchPlaceholder={t('hardware.selectAClip')}
                                loading={loadingClips}
                              />
                            </Box>
                          </>
                        )}
                      </Flex>
                    </Flex>
                  ))}
                  <Box width="100%">
                    <Flex direction="column" gap={2} width="100%" mb={4}>
                      {pendingClips[item.id]?.map((clipId, index) => {
                        const clipSerial = unassignedClips.find(
                          (c) => c.value === clipId
                        )?.label;
                        return (
                          <Flex
                            key={clipId}
                            direction="column"
                            gap={2}
                            width="100%"
                          >
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              pl="41px"
                              textAlign="left"
                            >
                              {t('clip')}{' '}
                              {(item.clips?.length || 0) + index + 1}
                            </Text>
                            <Flex alignItems="center" gap={25} width="100%">
                              <Box
                                cursor="pointer"
                                onClick={() => {
                                  setPendingClips((prev) => ({
                                    ...prev,
                                    [item.id]:
                                      prev[item.id]?.filter(
                                        (id) => id !== clipId
                                      ) || [],
                                  }));
                                  setSelectedClips((prev) => {
                                    const newState = { ...prev };
                                    delete newState[item.id];
                                    return newState;
                                  });
                                }}
                                position="relative"
                              >
                                <AleaUnlinkIcon size="16" />
                              </Box>
                              <Box flex={1}>
                                <AleaDropdownAlpha
                                  label=""
                                  value={clipSerial || ''}
                                  options={[{ label: clipSerial || '', value: clipSerial || '' }]}
                                  onChange={() => {}}
                                  placeholder={t('hardware.selectAClip')}
                                  searchPlaceholder={t('hardware.selectAClip')}
                                  isDisabled={true}
                                />
                              </Box>
                            </Flex>
                          </Flex>
                        );
                      })}
                      {showNewClip[item.id] && (
                        <Flex direction="column" gap={2} width="100%">
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            pl="41px"
                            textAlign="left"
                          >
                            {t('clip')}{' '}
                            {(item.clips?.length || 0) +
                              (pendingClips[item.id]?.length || 0) +
                              1}
                          </Text>
                          <Flex alignItems="center" gap={25} width="100%">
                            <Box width="16px" />
                            <Box flex={1}>
                              <AleaDropdownAlpha
                                label=""
                                value={selectedClips[item.id] || ''}
                                options={unassignedClips
                                  .filter(
                                    (clip) =>
                                      !pendingClips[item.id]?.includes(
                                        clip.value
                                      )
                                  )}
                                onChange={(value) => {
                                  const selectedOption = unassignedClips.find(
                                    (opt) => opt.value === value
                                  );
                                  if (selectedOption) {
                                    handleClipSelect(item.id, selectedOption.label);
                                  }
                                }}
                                placeholder={t('hardware.selectAClip')}
                                searchPlaceholder={t('hardware.selectAClip')}
                                loading={loadingClips}
                              />
                            </Box>
                          </Flex>
                        </Flex>
                      )}
                    </Flex>
                    {(item.clips?.length || 0) +
                      (pendingClips[item.id]?.length || 0) <
                      10 && (
                      <Flex
                        alignItems="center"
                        gap={2}
                        cursor="pointer"
                        pl="41px"
                        onClick={() =>
                          setShowNewClip((prev) => ({
                            ...prev,
                            [item.id]: true,
                          }))
                        }
                      >
                        <AleaPlusIcon size="16" />
                        <Text fontSize="sm" color="gray.500" textAlign="left">
                          {t('addAdditionalClip')}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>
              </Box>
            </GridItem>
          </Grid>
        </Box>
        <Flex justifyContent="flex-end" alignItems="center" gap="16px" mt={4}>
          {/* Cancel link - only show when there are pending changes */}
          {(pendingClips[item.id]?.length || 
            selectedResidents[item.id] || 
            pendingResidentUnlinks[item.id] || 
            item.clips?.some(clip => pendingClipUnlinks[clip.id]) ||
            item.clips?.some(clip => pendingClipUnlinks[clip.id] && selectedClips[`${item.id}_${clip.id}`])) && !isProgressModalOpen && (
            <Text
              color="gray.500"
              fontSize="sm"
              cursor="pointer"
              textDecoration="underline"
              onClick={() => {
                setPendingRowAction({ targetRowId: item.id, action: 'close' });
                setIsUnsavedChangesModalOpen(true);
              }}
              _hover={{ color: "gray.700" }}
            >
              {t('general.cancel')}
            </Text>
          )}

          <Button
            colorScheme="alea"
            variant="solid"
            size="lg"
            onClick={() => handleSubmitClips(item)}
            w="100px"
            h="50px"
            borderRadius="20px"
            isDisabled={
              isProgressModalOpen ||
              (!pendingClips[item.id]?.length && 
              !selectedResidents[item.id] && 
              !pendingResidentUnlinks[item.id] && 
              !item.clips?.some(clip => pendingClipUnlinks[clip.id]) &&
              !item.clips?.some(clip => pendingClipUnlinks[clip.id] && selectedClips[`${item.id}_${clip.id}`]))
            }
            _disabled={{
              bg: '#E2E8F0',
              opacity: 1,
              cursor: 'not-allowed',
            }}
          >
            <Image
              src={checkIcon}
              alt="Icon"
              h="21px"
              opacity={
                !pendingClips[item.id]?.length && 
                !selectedResidents[item.id] && 
                !pendingResidentUnlinks[item.id] && 
                !item.clips?.some(clip => pendingClipUnlinks[clip.id]) &&
                !item.clips?.some(clip => pendingClipUnlinks[clip.id] && selectedClips[`${item.id}_${clip.id}`])
                  ? 0.4
                  : 1
              }
            />
          </Button>
        </Flex>
      </Box>
    );
  };

  const handleRowClick = (item: ExtendedGateway) => {
    if (user?.role === UserRole.ORGANISATION_MANAGER) return;
    
    // Check if current expanded row has pending changes
    if (expandedRowId && hasPendingChanges(expandedRowId)) {
      // User is trying to close or switch - show confirmation modal
      if (expandedRowId === item.id) {
        // Trying to close the current row
        setPendingRowAction({ targetRowId: item.id, action: 'close' });
      } else {
        // Trying to switch to a different row
        setPendingRowAction({ targetRowId: item.id, action: 'switch' });
      }
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    
    // No pending changes, proceed normally
    setExpandedRowId(expandedRowId === item.id ? null : item.id);
  };

  return (
    <Box>
      <AleaTable<ExtendedGateway>
        columns={columns}
        data={gateways as ExtendedGateway[]}
        isLoading={isLoading}
        total={total}
        expandable={user?.role !== UserRole.ORGANISATION_MANAGER}
        expandedRowRender={renderExpandedRow}
        onRowClick={handleRowClick}
        noRecordsText={t('noRecordsFound')}
        usePagination={false}
        externalExpandedRowId={expandedRowId}
        firstColumnPadding={false}
      />
      
      {/* Unsaved Changes Modal */}
      <AleaConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleSaveAndProceed}
        onCancelButtonClick={handleDiscardAndProceed}
        title={t('saveChangesPopup')}
        confirmLabel={t('general.saveChanges')}
        cancelLabel={t('general.cancel')}
      />

      {/* Deactivate/Reactivate Confirmation Modal */}
      <AleaConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction) {
            if (confirmAction.type === 'deactivate') {
              executeDeactivateGateway(confirmAction.gateway);
            } else {
              executeReactivateGateway(confirmAction.gateway);
            }
            setConfirmAction(null);
          }
        }}
        onCancelButtonClick={() => setConfirmAction(null)}
        title={t('saveChangesPopup')}
        confirmLabel={t('general.yes')}
        cancelLabel={t('general.cancel')}
      />

      {/* Progress Modal */}
      <AleaProgressModal
        isOpen={isProgressModalOpen}
        message={progressMessage}
      />
    </Box>
  );
});

HardwareTable.displayName = 'HardwareTable';

export default HardwareTable;
